package com.vizio.redwolf.auth.client

import com.vizio.redwolf.auth.model.AccountAbuseFormInfo
import com.vizio.redwolf.auth.model.AccountAuthCodeRequestBody
import com.vizio.redwolf.auth.model.AccountAuthCodeRequestInfo
import com.vizio.redwolf.auth.model.AccountEmailExistsSDKRequestBody
import com.vizio.redwolf.auth.model.AccountForgotPasswordSDKInfo
import com.vizio.redwolf.auth.model.AccountRefreshTokenInfo
import com.vizio.redwolf.auth.model.AccountRegisterProductInfo
import com.vizio.redwolf.auth.model.AccountSerialNumberInfo
import com.vizio.redwolf.auth.model.AccountShippingAddress
import com.vizio.redwolf.auth.model.AccountUnsubscribeSDKInfo
import com.vizio.redwolf.auth.model.AccountUserEmailPreference
import com.vizio.redwolf.auth.model.SwapOneOrAllDevicesClientRequestInfo
import com.vizio.redwolf.auth.model.ConvertToWalmartAccountWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.CreateAccount2ClientRequestInfo
import com.vizio.redwolf.auth.model.CreateAccountClientRequestInfo
import com.vizio.redwolf.auth.model.EmailVerifiedRequestInfo
import com.vizio.redwolf.auth.model.LoginClientRequestInfo
import com.vizio.redwolf.auth.model.MigrateGuestClientRequestInfo
import com.vizio.redwolf.auth.model.RequiresVerificationRequestInfo
import com.vizio.redwolf.auth.model.SendTermsRequestInfo
import com.vizio.redwolf.auth.model.SendVerificationEmailRequestInfo
import com.vizio.redwolf.auth.model.SocialLoginClientRequestInfo
import com.vizio.redwolf.auth.model.UpdateAccountRegisteredProductsInfo
import com.vizio.redwolf.auth.model.UpdateEmailClientRequestInfo
import com.vizio.redwolf.auth.model.UpdateEmailV2ClientRequestInfo
import com.vizio.redwolf.auth.model.UpdatePasswordInfo
import com.vizio.redwolf.auth.model.UpdateUserDetailsClientRequestInfo
import com.vizio.redwolf.auth.model.UserDetailsRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountWithTokenClientRequestInfo
import com.vizio.redwolf.auth.model.CreateWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.DeleteVizioAccountRequestInfo
import com.vizio.redwolf.auth.model.WalmartLoginClientRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPRequestInfo
import com.vizio.redwolf.auth.model.VerifyEmailOTPRequestInfo
import com.vizio.redwolf.auth.model.WalmartAccountDeviceRelinkClientRequestInfo
import com.vizio.redwolf.auth.model.VerifyEmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.WalmartLoginViaSetupCodeClientRequestInfo
import com.vizio.redwolf.auth.model.WebEmailVerificationClientRequestInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.SUBSCRIPTION_API_KEY
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.applyUserBearerToken
import com.vizio.redwolf.utils.buildHttpRequest
import com.vizio.redwolf.utils.safeHttpCall
import com.vizio.redwolf.utils.setSentryHeader
import com.vizio.redwolf.utils.setTraceId
import io.ktor.client.HttpClient
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.headers
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.encodeToJsonElement

internal class AccountHttpClient(private val payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) {
    private val accountPath = payEnv.payAuthPath
    private val accountApiKey = payEnv.vizioApiSubscriptionKey
    private val httpClient: HttpClient = clientConfig.clientProvider.httpClient
    private val json = Json { encodeDefaults = true }

    suspend fun createAccount(clientRequestInfo: CreateAccountClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        return createAccount(requestBody, traceId)
    }

    suspend fun createAccount(clientRequestInfo: CreateAccount2ClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        return createAccount(requestBody, traceId)
    }

    private suspend fun createAccount(requestBody: JsonElement, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun login(clientRequestInfo: LoginClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        return login(requestBody, traceId)
    }

    suspend fun login(clientRequestInfo: SocialLoginClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        return login(requestBody, traceId)
    }

    private suspend fun login(requestBody: JsonElement, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users/authorizations")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun requestUserDetails(clientRequestInfo: UserDetailsRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(clientRequestInfo.accessToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updateUserDetails(accessToken: String, clientRequestInfo: UpdateUserDetailsClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Put
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun checkIfEmailVerified(clientRequestInfo: EmailVerifiedRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email/verified")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(clientRequestInfo.accessToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun checkIfRequiresEmailVerification(clientRequestInfo: RequiresVerificationRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email/requires-verification")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(clientRequestInfo.accessToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun sendVerificationEmail(clientRequestInfo: SendVerificationEmailRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/verification-email")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(clientRequestInfo.accessToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun sendTermsEmail(clientRequestInfo: SendTermsRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/terms-email")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(clientRequestInfo.accessToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun migrateGuestAccount(accessToken: String, clientRequestInfo: MigrateGuestClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/full-account")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun migrateGuestAccountV2(accessToken: String, clientRequestInfo: MigrateGuestClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users/full-account")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updateEmail(accessToken: String, clientRequestInfo: UpdateEmailClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Put
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updateEmailV2(accessToken: String, clientRequestInfo: UpdateEmailV2ClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/v2/users/email")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Put
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    /**
     * This is WEB(i.e vizio.com) ONLY API. THis is NOT to be used by Mobile.
     */
    suspend fun webVerifyToken(accessToken: String, clientRequestInfo: WebEmailVerificationClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(clientRequestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email/verifications")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun logout(authToken: String, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/authorizations")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Delete
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun emailExists(emailExistsRequestBody: AccountEmailExistsSDKRequestBody, traceId: String?): HttpResult<HttpResponse> {
        val emailExistBody = json.encodeToJsonElement(emailExistsRequestBody)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email/exists")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(emailExistBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun refreshToken(refreshTokenInfo: AccountRefreshTokenInfo, traceId: String?): HttpResult<HttpResponse> {
        val refreshTokenBody = json.encodeToJsonElement(refreshTokenInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/refresh-token")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(refreshTokenBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun requestJwt(authToken: String, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/jwt")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun resetPasswordRequest(email: AccountForgotPasswordSDKInfo, traceId: String?): HttpResult<HttpResponse> {
        val resetPwRequestInfoBody = json.encodeToJsonElement(email)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/passwords")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(resetPwRequestInfoBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updatePassword(authToken: String, pwInfo: UpdatePasswordInfo, traceId: String?): HttpResult<HttpResponse> {
        val resetPwInfoBody = json.encodeToJsonElement(pwInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/password")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setBody(resetPwInfoBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun getShippingAddress(authToken: String, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/shipping-address")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updateShippingAddress(authToken: String, newAddress: AccountShippingAddress, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val addressInfoBody = json.encodeToJsonElement(newAddress)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/shipping-address")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setBody(addressInfoBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun getRegisteredProducts(authToken: String, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/product-registrations")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun unregisterProduct(authToken: String, serialNumber: String, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/product-registrations/$serialNumber")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Delete
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

     suspend fun updateRegisteredProducts(authToken: String, serialNumbers: List<String>, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val updateRegisteredProductInfoBody = json.encodeToJsonElement(UpdateAccountRegisteredProductsInfo(serialNumbers))
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/product-registrations")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setBody(updateRegisteredProductInfoBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

     suspend fun registerProduct(authToken: String, registerProductInfo: AccountRegisterProductInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val registeredProductInfoBody = Json.encodeToJsonElement(registerProductInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/product-registrations")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setBody(registeredProductInfoBody)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun getEmailPreference(authToken: String, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email-preferences")
            }
            headers {
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setSentryHeader(sentryHeader)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Get
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun updateEmailPreference(authToken: String, emailPreference: AccountUserEmailPreference, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val emailPreferenceJson = json.encodeToJsonElement(emailPreference)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/email-preferences")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setBody(emailPreferenceJson)
                applyAppConfig(appConfig)
                setTraceId(traceId)
                applyTimeout(clientConfig.timeout)
            }
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun unsubscribe(unsubscribeInfo: AccountUnsubscribeSDKInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val unsubInfo = json.encodeToJsonElement(unsubscribeInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/out-outs")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(unsubInfo)
            setSentryHeader(sentryHeader)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun createAbuseReport(authToken: String, abuseFormInfo: AccountAbuseFormInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val abuseForm = json.encodeToJsonElement(abuseFormInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/abuse-reports")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
            }
            setBody(abuseForm)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun validateSerialNumber(authToken: String, serialNumberInfo: AccountSerialNumberInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(serialNumberInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/serial-numbers/validate")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(authToken)
            setBody(requestBody)
            setSentryHeader(sentryHeader)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun requestAuthCode(accountAuthCodeRequestInfo: AccountAuthCodeRequestInfo, sentryHeader: String?, traceId: String?): HttpResult<HttpResponse> {
        val accountAuthCodeRequestBody = AccountAuthCodeRequestBody(
            clientId = accountAuthCodeRequestInfo.clientId,
            redirectUri = accountAuthCodeRequestInfo.redirectUri,
            state = accountAuthCodeRequestInfo.state
        )
        val requestBody = json.encodeToJsonElement(accountAuthCodeRequestBody)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/users/authorization-code")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accountAuthCodeRequestInfo.accessToken)
            setBody(requestBody)
            setSentryHeader(sentryHeader)
            applyAppConfig(appConfig)
            setTraceId(traceId)
            applyTimeout(clientConfig.timeout)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun walmartLogin(requestInfo: WalmartLoginClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/token")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun walmartLoginWithSetupCode(requestInfo: WalmartLoginViaSetupCodeClientRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/token/setup-code")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun createWalmartAccount(requestInfo: CreateWalmartAccountRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/create")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun convertToWalmartAccount(requestInfo: ConvertWalmartAccountRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/convert/email")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun convertToWalmartAccountWithAccessToken(requestInfo: ConvertWalmartAccountWithTokenClientRequestInfo, accessToken: String, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/convert")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun convertToWalmartAccountWithSetupCode(requestInfo: ConvertToWalmartAccountWithSetupCodeRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/convert/setup-code")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun deleteVizioAccount(requestInfo: DeleteVizioAccountRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/delete")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun requestEmailOTP(requestInfo: EmailOTPRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/otp")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun verifyEmailOTP(requestInfo: VerifyEmailOTPRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/otp/verify")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun requestEmailOTPWithSetupCode(requestInfo: EmailOTPWithSetupCodeRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/otp/setup-code")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun verifyEmailOTPWithSetupCode(requestInfo: VerifyEmailOTPWithSetupCodeRequestInfo, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/otp/setup-code/verify")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun walmartAccountDeviceRelink(requestInfo: WalmartAccountDeviceRelinkClientRequestInfo, accessToken: String, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/device/relink")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }

    suspend fun swapOneOrAllDevices(requestInfo: SwapOneOrAllDevicesClientRequestInfo, accessToken: String, traceId: String?): HttpResult<HttpResponse> {
        val requestBody = json.encodeToJsonElement(requestInfo)
        val httpRequestBuilder = HttpRequestBuilder().apply {
            url {
                protocol = URLProtocol.HTTPS
                host = payEnv.payHost
                path("$accountPath/walmart/account/device/relink/claim")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                append(SUBSCRIPTION_API_KEY, accountApiKey)
            }
            applyUserBearerToken(accessToken)
            setBody(requestBody)
            applyAppConfig(appConfig)
            applyTimeout(clientConfig.timeout)
            setTraceId(traceId)
            method = HttpMethod.Post
        }
        return safeHttpCall(httpRequestInfo = httpRequestBuilder.buildHttpRequest()) {
            httpClient.request(httpRequestBuilder)
        }
    }
}